import { useState, useEffect } from 'react';
import { Calendar, Plus, Clock, Users, Send, Edit2, X, Check, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import DateFilter from './DateFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SchedulesEnhanced = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('todo');
  const [schedules, setSchedules] = useState([]);
  const [submittedSchedules, setSubmittedSchedules] = useState([]);
  const [allSubmittedSchedules, setAllSubmittedSchedules] = useState([]);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState([]);
  const [expandedSchedules, setExpandedSchedules] = useState({});
  const [editingLog, setEditingLog] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [toilBalances, setToilBalances] = useState({});
  const [toilSplits, setToilSplits] = useState({});
  const [toilUsage, setToilUsage] = useState({});
  const [employeeContractedHours, setEmployeeContractedHours] = useState({});
  const [showToilDialog, setShowToilDialog] = useState(false);
  const [currentScheduleForToil, setCurrentScheduleForToil] = useState(null);
  const [scheduleConflicts, setScheduleConflicts] = useState({});
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    scheduled_start_time: '',
    scheduled_finish_time: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const applyDateFilter = (data, filter) => {
    if (!filter.start || !filter.end) {
      setSubmittedSchedules(data);
      return;
    }

    const filtered = data.filter(schedule => {
      const scheduleEnd = schedule.end_date;
      return scheduleEnd >= filter.start && scheduleEnd <= filter.end;
    });
    setSubmittedSchedules(filtered);
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    applyDateFilter(allSubmittedSchedules, filter);
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [todoRes, submittedRes, employeesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/schedules/to-do`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/schedules/submitted`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${BACKEND_URL}/api/employees/my-team`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (todoRes.ok) {
        const data = await todoRes.json();
        setSchedules(data);
      }

      if (submittedRes.ok) {
        const data = await submittedRes.json();
        setAllSubmittedSchedules(data);
        applyDateFilter(data, dateFilter);
      }

      if (employeesRes.ok) {
        const data = await employeesRes.json();
        // Filter out managers and admins from the employee list
        const nonManagerEmployees = data.filter(emp => 
          emp.role !== 'manager' && emp.role !== 'admin'
        );
        setEmployees(nonManagerEmployees);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const checkScheduleConflicts = async () => {
    if (!formData.start_date || !formData.end_date || selectedEmployees.length === 0) {
      setScheduleConflicts({});
      return;
    }

    setCheckingConflicts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/schedules/check-conflicts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employee_ids: selectedEmployees,
          start_date: formData.start_date,
          end_date: formData.end_date
        })
      });

      if (res.ok) {
        const data = await res.json();
        setScheduleConflicts(data.conflicts || {});
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
    } finally {
      setCheckingConflicts(false);
    }
  };

  // Check for conflicts whenever dates or selected employees change
  useEffect(() => {
    if (showCreateDialog) {
      checkScheduleConflicts();
    }
  }, [formData.start_date, formData.end_date, selectedEmployees, showCreateDialog]);

  const handleCreateSchedules = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee');
      return;
    }

    if (!formData.start_date || !formData.end_date || !formData.scheduled_start_time || !formData.scheduled_finish_time) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/schedules/bulk-create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employee_ids: selectedEmployees,
          ...formData
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data?.message || 'Schedules created successfully');
        setShowCreateDialog(false);
        setSelectedEmployees([]);
        setSelectedDepartment('all');
        setFormData({ start_date: '', end_date: '', scheduled_start_time: '', scheduled_finish_time: '' });
        fetchData();
      } else {
        const error = await res.json().catch(() => ({ detail: 'Failed to create schedules' }));
        toast.error(error.detail || 'Failed to create schedules');
      }
    } catch (error) {
      console.error('Error creating schedules:', error);
      toast.error('Network error. Please try again.');
    }
  };

  const handleEditTimeLog = async (employeeId, date, clockIn, clockOut) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/time-logs/edit?employee_id=${employeeId}&date=${date}&clock_in_time=${clockIn || ''}&clock_out_time=${clockOut || ''}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Time log updated successfully');
        setEditingLog(null);
        fetchData();
      } else {
        toast.error('Failed to update time log');
      }
    } catch (error) {
      console.error('Error editing time log:', error);
      toast.error('Failed to update time log');
    }
  };

  const handleDeleteSchedule = async (scheduleId, employeeName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the schedule for ${employeeName}?\n\nThis action cannot be undone.`
    );
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Schedule deleted successfully');
        fetchData();
      } else {
        const error = await res.json().catch(() => ({ message: 'Failed to delete schedule' }));
        toast.error(error.message || 'Failed to delete schedule');
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedScheduleIds.length === 0) {
      toast.error('Please select at least one schedule');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/timesheets/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ schedule_ids: selectedScheduleIds })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setSelectedScheduleIds([]);
        fetchData();
      } else {
        toast.error('Failed to submit timesheets');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit timesheets');
    }
  };

  const toggleScheduleSelection = (scheduleId) => {
    setSelectedScheduleIds(prev =>
      prev.includes(scheduleId) ? prev.filter(id => id !== scheduleId) : [...prev, scheduleId]
    );
  };

  const fetchToilBalance = async (employeeId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/employees/${employeeId}/toil-balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setToilBalances(prev => ({ ...prev, [employeeId]: data.toil_balance }));
      }
    } catch (error) {
      console.error('Error fetching TOIL balance:', error);
    }
  };

  const handleToilSplit = (scheduleId, overtime) => {
    // Initialize split if not exists
    if (!toilSplits[scheduleId]) {
      setToilSplits(prev => ({
        ...prev,
        [scheduleId]: { paid: overtime, toil: 0 }
      }));
    }
    setCurrentScheduleForToil(scheduleId);
    setShowToilDialog(true);
  };

  const updateToilSplit = async (scheduleId) => {
    const split = toilSplits[scheduleId];
    if (!split) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/schedules/${scheduleId}/toil-split`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          overtime_paid: split.paid,
          overtime_toil: split.toil
        })
      });

      if (res.ok) {
        toast.success(`Saved! ${split.paid}h will be paid, ${split.toil}h will be banked as TOIL when submitted`);
        fetchData();
      } else {
        toast.error('Failed to update TOIL split');
      }
    } catch (error) {
      console.error('Error updating TOIL split:', error);
      toast.error('Failed to update TOIL split');
    }
  };

  const handleUseToil = async (scheduleId, employeeId) => {
    const amount = toilUsage[scheduleId] || 0;
    if (amount <= 0) {
      toast.error('Please enter a valid TOIL amount');
      return;
    }

    const balance = toilBalances[employeeId] || 0;
    if (amount > balance) {
      toast.error(`Insufficient TOIL balance. Available: ${balance}h`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/schedules/${scheduleId}/use-toil`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ toil_used: amount })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchToilBalance(employeeId);
        fetchData();
        setToilUsage(prev => ({ ...prev, [scheduleId]: 0 }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to use TOIL');
      }
    } catch (error) {
      console.error('Error using TOIL:', error);
      toast.error('Failed to use TOIL');
    }
  };

  const handleRemoveToil = async (scheduleId, employeeId) => {
    if (!window.confirm('Remove applied TOIL? This will restore the TOIL to the employee\'s balance.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/schedules/${scheduleId}/remove-toil`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Removed! ${data.toil_restored}h restored to employee's balance`);
        fetchToilBalance(employeeId);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to remove TOIL');
      }
    } catch (error) {
      console.error('Error removing TOIL:', error);
      toast.error('Failed to remove TOIL');
    }
  };

  const toggleScheduleExpand = async (scheduleId) => {
    const isCurrentlyExpanded = expandedSchedules[scheduleId];
    setExpandedSchedules(prev => ({ ...prev, [scheduleId]: !prev[scheduleId] }));
    
    // Fetch employee data when expanding
    if (!isCurrentlyExpanded) {
      const schedule = [...schedules, ...submittedSchedules].find(s => s.id === scheduleId);
      if (schedule) {
        // Fetch TOIL balance
        fetchToilBalance(schedule.employee_id);
        
        // Fetch employee's contracted hours if not already fetched
        if (!employeeContractedHours[schedule.employee_id]) {
          try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${BACKEND_URL}/api/employees`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
              const allEmployees = await res.json();
              const employee = allEmployees.find(e => e.id === schedule.employee_id);
              if (employee) {
                setEmployeeContractedHours(prev => ({
                  ...prev,
                  [schedule.employee_id]: employee.contracted_hours || 40
                }));
              }
            }
          } catch (error) {
            console.error('Error fetching employee contracted hours:', error);
            setEmployeeContractedHours(prev => ({
              ...prev,
              [schedule.employee_id]: 40
            }));
          }
        }
        
        // Initialize TOIL split if overtime exists
        const contracted = employeeContractedHours[schedule.employee_id] || 40;
        const entries = generateTimeLogPreview(schedule);
        const overtime = Math.max(0, entries.reduce((sum, e) => sum + parseFloat(e.actualWorkedHours), 0) - contracted);
        if (overtime > 0 && !toilSplits[scheduleId]) {
          setToilSplits(prev => ({
            ...prev,
            [scheduleId]: { 
              paid: schedule.overtime_paid || overtime, 
              toil: schedule.overtime_toil || 0 
            }
          }));
        }
        // Initialize TOIL usage
        if (!toilUsage[scheduleId]) {
          setToilUsage(prev => ({ ...prev, [scheduleId]: schedule.toil_used || 0 }));
        }
      }
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev =>
      prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId]
    );
  };

  const handleSelectAllEmployees = () => {
    const filtered = getFilteredEmployees();
    if (selectedEmployees.length === filtered.length) {
      // Deselect all
      setSelectedEmployees([]);
    } else {
      // Select all filtered employees
      setSelectedEmployees(filtered.map(emp => emp.id));
    }
  };

  const getFilteredEmployees = () => {
    if (selectedDepartment === 'all') {
      return employees;
    }
    return employees.filter(emp => emp.department === selectedDepartment);
  };

  const getDepartments = () => {
    const depts = [...new Set(employees.map(emp => emp.department))].filter(Boolean);
    return depts.sort();
  };

  const handleDepartmentChange = (dept) => {
    setSelectedDepartment(dept);
    // Clear selections when changing department
    setSelectedEmployees([]);
  };

  const generateTimeLogPreview = (schedule) => {
    const entries = [];
    const startDate = new Date(schedule.start_date);
    const endDate = new Date(schedule.end_date);
    const timeLogs = schedule.time_logs || [];
    const clockSessions = schedule.clock_sessions || [];
    
    // Group time logs and clock sessions by date - ALLOW MULTIPLE PER DATE
    const timeLogsByDate = {};
    timeLogs.forEach(log => {
      if (!timeLogsByDate[log.date]) timeLogsByDate[log.date] = [];
      timeLogsByDate[log.date].push(log);
    });
    
    // Also use clock_sessions if available (for kiosk-generated sessions)
    clockSessions.forEach(session => {
      if (!timeLogsByDate[session.date]) timeLogsByDate[session.date] = [];
      // Convert session format to time_log format for compatibility
      timeLogsByDate[session.date].push({
        date: session.date,
        clock_in_timestamp: session.clock_in_timestamp,
        clock_out_timestamp: session.clock_out_timestamp,
        id: session.id
      });
    });

    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const logsForDate = timeLogsByDate[dateStr] || [];
      
      // Process each log entry for this date (could be multiple)
      if (logsForDate.length === 0) {
        // No logs for this date - create a missing entry
        let clockIn = null;
        let clockOut = null;
        let status = 'missing';
        let scheduledHours = 0;
        let rawHours = 0;
        let actualWorkedHours = 0;
        let overtime = 0;

        // Calculate scheduled hours
        if (schedule.scheduled_start_time && schedule.scheduled_finish_time) {
          const [startH, startM] = schedule.scheduled_start_time.split(':').map(Number);
          const [endH, endM] = schedule.scheduled_finish_time.split(':').map(Number);
          scheduledHours = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
        }

        entries.push({ 
          date: dateStr, 
          clockIn, 
          clockOut, 
          status,
          scheduledHours: scheduledHours.toFixed(2), // Show scheduled hours for missing days
          rawHours: rawHours.toFixed(2),
          paidBreaks: (0).toFixed(2),
          unpaidBreaks: (0).toFixed(2),
          actualWorkedHours: actualWorkedHours.toFixed(2),
          overtime: overtime.toFixed(2)
        });
      } else {
        // Process each log entry for this date
        logsForDate.forEach((log, index) => {
          let clockIn = null;
          let clockOut = null;
          let status = 'missing';
          let scheduledHours = 0;
          let rawHours = 0;
          let actualWorkedHours = 0;
          let overtime = 0;

          // Calculate scheduled hours
          if (schedule.scheduled_start_time && schedule.scheduled_finish_time) {
            const [startH, startM] = schedule.scheduled_start_time.split(':').map(Number);
            const [endH, endM] = schedule.scheduled_finish_time.split(':').map(Number);
            scheduledHours = ((endH * 60 + endM) - (startH * 60 + startM)) / 60;
          }

          if (log && log.clock_in_timestamp) {
            clockIn = new Date(log.clock_in_timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
            
            // Compare time only (ignore date) - extract hours and minutes
            const actualTime = new Date(log.clock_in_timestamp);
            const actualHour = actualTime.getUTCHours();
            const actualMinute = actualTime.getUTCMinutes();
            const actualTimeInMinutes = actualHour * 60 + actualMinute;
            
            const [schedHour, schedMinute] = schedule.scheduled_start_time.split(':').map(Number);
            const scheduledTimeInMinutes = schedHour * 60 + schedMinute;

            // If they clocked in, they're either on time or late (never missing)
            if (actualTimeInMinutes <= scheduledTimeInMinutes) status = 'on_time';
            else status = 'late';  // Any clock in after scheduled time is late
          }

          if (log && log.clock_out_timestamp) {
            clockOut = new Date(log.clock_out_timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
          }

          // Calculate hours if both clock in/out exist
          let paidBreaks = 0;
          let unpaidBreaks = 0;
          
          if (clockIn && clockOut) {
            const [inH, inM] = clockIn.split(':').map(Number);
            const [outH, outM] = clockOut.split(':').map(Number);
            rawHours = ((outH * 60 + outM) - (inH * 60 + inM)) / 60;
            
            // Apply automatic break rules
            // Morning break: 10:00-10:15 (15 min, unpaid) - if shift >= 4h and working during break
            if (rawHours >= 4 && inH < 10 || (inH === 10 && inM === 0)) {
              if (outH > 10 || (outH === 10 && outM >= 15)) {
                unpaidBreaks += 0.25;
              }
            }
            
            // Lunch break: 12:30-13:00 (30 min, paid) - if shift >= 5h and working during break
            if (rawHours >= 5) {
              if ((inH < 12 || (inH === 12 && inM <= 30)) && (outH > 13 || (outH === 13 && outM === 0))) {
                paidBreaks += 0.5;
              }
            }
            
            // Afternoon break: 15:00-15:15 (15 min, unpaid) - if shift >= 7h and working during break
            if (rawHours >= 7 && (inH < 15 || (inH === 15 && inM === 0))) {
              if (outH > 15 || (outH === 15 && outM >= 15)) {
                unpaidBreaks += 0.25;
              }
            }
            
            actualWorkedHours = rawHours - unpaidBreaks;
            overtime = 0; // No daily overtime
          }

          entries.push({ 
            date: index === 0 ? dateStr : '-', // Only show date on first row
            clockIn, 
            clockOut, 
            status: index === 0 ? status : '-', // Only show status on first row
            scheduledHours: index === 0 ? scheduledHours.toFixed(2) : '-', // Only show scheduled hours on first row
            rawHours: rawHours.toFixed(2),
            paidBreaks: paidBreaks.toFixed(2),
            unpaidBreaks: unpaidBreaks.toFixed(2),
            actualWorkedHours: actualWorkedHours.toFixed(2),
            overtime: overtime.toFixed(2)
          });
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return entries;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Create/View Schedules</h1>
            <p className="text-sm text-gray-600 mt-1">Manage employee work schedules and submit to finance</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {activeTab === 'todo' && selectedScheduleIds.length > 0 && (
              <Button
                onClick={handleBulkSubmit}
                className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit {selectedScheduleIds.length} to Finance
              </Button>
            )}
            {activeTab === 'todo' && (
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Schedule
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('todo')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'todo'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          To-Do ({schedules.length})
        </button>
        <button
          onClick={() => setActiveTab('submitted')}
          className={`px-4 py-2 font-medium ${
            activeTab === 'submitted'
              ? 'border-b-2 border-emerald-600 text-emerald-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Submitted ({submittedSchedules.length})
        </button>
      </div>

      {/* Date Filter for Submitted Tab */}
      {activeTab === 'submitted' && (
        <div className="mt-4">
          <DateFilter onFilterChange={handleDateFilterChange} />
        </div>
      )}

      <div className="space-y-4">
        {((activeTab === 'todo' && schedules.length === 0) || (activeTab === 'submitted' && submittedSchedules.length === 0)) ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">{activeTab === 'todo' ? 'No schedules to review.' : 'No submitted schedules yet.'}</p>
          </div>
        ) : (
          (activeTab === 'todo' ? schedules : submittedSchedules).map(schedule => {
            const isExpanded = expandedSchedules[schedule.id];
            const entries = generateTimeLogPreview(schedule);

            return (
              <div key={schedule.id} className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-start gap-4">
                  {activeTab === 'todo' && (
                    <input
                      type="checkbox"
                      checked={selectedScheduleIds.includes(schedule.id)}
                      onChange={() => toggleScheduleSelection(schedule.id)}
                      className="w-5 h-5 text-emerald-600 mt-1"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-lg font-semibold">{schedule.employee_name}</h3>
                        <p className="text-sm text-gray-500">{schedule.department}</p>
                        <p className="text-sm text-gray-600 mt-1">
                          {new Date(schedule.start_date).toLocaleDateString()} - {new Date(schedule.end_date).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600">
                          Scheduled: {schedule.scheduled_start_time} - {schedule.scheduled_finish_time}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteSchedule(schedule.id, schedule.employee_name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleScheduleExpand(schedule.id)}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {isExpanded ? 'Hide' : 'Show'} Details
                        </Button>
                      </div>
                    </div>

                    {isExpanded && (
                      <>
                        <div className="mt-4 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-2 py-2 text-left">Date</th>
                                <th className="px-2 py-2 text-left">Clock In</th>
                                <th className="px-2 py-2 text-left">Clock Out</th>
                                <th className="px-2 py-2 text-left">Status</th>
                                <th className="px-2 py-2 text-right">Sch.Hrs</th>
                                <th className="px-2 py-2 text-right">Raw Hrs</th>
                                <th className="px-2 py-2 text-right">Paid Brk</th>
                                <th className="px-2 py-2 text-right">Unpaid Brk</th>
                                <th className="px-2 py-2 text-right">Worked Hrs</th>
                                <th className="px-2 py-2 text-right">O/T</th>
                                {activeTab === 'todo' && (
                                  <th className="px-2 py-2 text-center">Edit</th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {entries.map((entry, idx) => (
                                <tr key={idx} className="border-t hover:bg-gray-50">
                                  <td className="px-2 py-2">
                                    {entry.date === '-' ? '-' : new Date(entry.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  </td>
                                  <td className="px-2 py-2">{entry.clockIn || '-'}</td>
                                  <td className="px-2 py-2">{entry.clockOut || '-'}</td>
                                  <td className="px-2 py-2">
                                    {entry.status === '-' ? '-' : (
                                      <span className={`px-2 py-1 rounded text-xs ${
                                        entry.status === 'on_time' ? 'bg-green-100 text-green-700' :
                                        entry.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>
                                        {entry.status === 'on_time' ? 'On Time' : entry.status === 'late' ? 'Late' : 'Missing'}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-2 text-right">{entry.scheduledHours === '-' ? '-' : `${entry.scheduledHours}h`}</td>
                                  <td className="px-2 py-2 text-right">{entry.rawHours}h</td>
                                  <td className="px-2 py-2 text-right">{entry.paidBreaks}h</td>
                                  <td className="px-2 py-2 text-right">{entry.unpaidBreaks}h</td>
                                  <td className="px-2 py-2 text-right font-semibold">{entry.actualWorkedHours}h</td>
                                  <td className="px-2 py-2 text-right text-blue-600 font-semibold">{entry.overtime}h</td>
                                  {activeTab === 'todo' && (
                                    <td className="px-2 py-2 text-center">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setEditingLog({ 
                                          employeeId: schedule.employee_id, 
                                          date: entry.date, 
                                          clockIn: entry.clockIn || '', 
                                          clockOut: entry.clockOut || '' 
                                        })}
                                      >
                                        <Edit2 className="w-3 h-3" />
                                      </Button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Weekly Summary */}
                        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-semibold text-sm">Weekly Summary</h4>
                            <div className="text-xs bg-white px-3 py-1 rounded-full border border-blue-300">
                              <span className="text-gray-600">TOIL Available: </span>
                              <span className="font-bold text-blue-600">
                                {(toilBalances[schedule.employee_id] || 0).toFixed(1)}h
                              </span>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 text-sm">
                            <div>
                              <p className="text-gray-600 text-xs">Raw Hours</p>
                              <p className="font-bold text-lg">
                                {entries.reduce((sum, e) => sum + parseFloat(e.rawHours), 0).toFixed(2)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600 text-xs">Worked Hours</p>
                              <p className="font-bold text-lg text-green-600">
                                {entries.reduce((sum, e) => sum + parseFloat(e.actualWorkedHours), 0).toFixed(2)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600 text-xs">Paid Breaks</p>
                              <p className="font-bold text-lg">
                                {entries.reduce((sum, e) => sum + parseFloat(e.paidBreaks), 0).toFixed(2)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600 text-xs">Unpaid Breaks</p>
                              <p className="font-bold text-lg">
                                {entries.reduce((sum, e) => sum + parseFloat(e.unpaidBreaks), 0).toFixed(2)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600 text-xs">TOIL Used</p>
                              <p className="font-bold text-lg text-purple-600">
                                {(schedule.toil_used || 0).toFixed(2)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600 text-xs">Overtime</p>
                              <p className="font-bold text-lg text-blue-600">
                                {Math.max(0, entries.reduce((sum, e) => sum + parseFloat(e.actualWorkedHours), 0) - (employeeContractedHours[schedule.employee_id] || 40)).toFixed(2)}h
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600 text-xs">Contracted</p>
                              <p className="font-bold text-lg">{(employeeContractedHours[schedule.employee_id] || 40).toFixed(2)}h</p>
                            </div>
                          </div>
                        </div>

                        {/* TOIL Management Section - Only show in To-Do tab */}
                        {activeTab === 'todo' && (() => {
                          const totalWorked = entries.reduce((sum, e) => sum + parseFloat(e.actualWorkedHours), 0);
                          const contracted = employeeContractedHours[schedule.employee_id] || 40;
                          const overtime = Math.max(0, totalWorked - contracted);
                          const shortfall = Math.max(0, contracted - totalWorked);
                          const employeeToilBalance = toilBalances[schedule.employee_id] || 0;
                          const currentSplit = toilSplits[schedule.id] || { paid: overtime, toil: 0 };
                          const currentToilUsage = toilUsage[schedule.id] || 0;

                          return (
                            <div className="mt-4 space-y-3">
                              {/* Overtime Split */}
                              {overtime > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                  <h5 className="font-semibold text-sm mb-3 text-amber-900">Overtime Management</h5>
                                  <p className="text-xs text-amber-700 mb-3">Split {overtime.toFixed(2)}h overtime between paid hours and TOIL banking</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                      <Label className="text-xs">To be Paid (hours)</Label>
                                      <Input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max={overtime}
                                        value={currentSplit.paid}
                                        onChange={(e) => {
                                          const paid = parseFloat(e.target.value) || 0;
                                          setToilSplits(prev => ({
                                            ...prev,
                                            [schedule.id]: { paid, toil: Math.max(0, overtime - paid) }
                                          }));
                                        }}
                                        className="mt-1"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">To be TOIL (hours)</Label>
                                      <Input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max={overtime}
                                        value={currentSplit.toil}
                                        onChange={(e) => {
                                          const toil = parseFloat(e.target.value) || 0;
                                          setToilSplits(prev => ({
                                            ...prev,
                                            [schedule.id]: { toil, paid: Math.max(0, overtime - toil) }
                                          }));
                                        }}
                                        className="mt-1"
                                      />
                                    </div>
                                  </div>
                                  <div className="mt-3 flex justify-between items-center text-xs">
                                    <span className="text-amber-700">
                                      Total: {(currentSplit.paid + currentSplit.toil).toFixed(2)}h / {overtime.toFixed(2)}h
                                    </span>
                                    <Button
                                      size="sm"
                                      onClick={() => updateToilSplit(schedule.id)}
                                      disabled={(currentSplit.paid + currentSplit.toil).toFixed(2) !== overtime.toFixed(2)}
                                      className="bg-amber-600 hover:bg-amber-700"
                                    >
                                      Save Split
                                    </Button>
                                  </div>
                                </div>
                              )}

                              {/* TOIL Usage for Short Weeks */}
                              {shortfall > 0 && employeeToilBalance > 0 && (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                  <h5 className="font-semibold text-sm mb-3 text-purple-900">Use TOIL to Top Up</h5>
                                  <p className="text-xs text-purple-700 mb-3">
                                    Shortfall: <span className="font-semibold">{shortfall.toFixed(2)}h</span> (Worked {totalWorked.toFixed(2)}h of {contracted}h contracted)
                                  </p>
                                  <div className="flex gap-3 items-end">
                                    <div className="flex-1">
                                      <Label className="text-xs">Use TOIL (hours)</Label>
                                      <Input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        max={Math.min(shortfall, employeeToilBalance)}
                                        value={currentToilUsage}
                                        onChange={(e) => {
                                          const val = parseFloat(e.target.value) || 0;
                                          setToilUsage(prev => ({ ...prev, [schedule.id]: val }));
                                        }}
                                        className="mt-1"
                                        placeholder={`Max: ${Math.min(shortfall, employeeToilBalance).toFixed(2)}h`}
                                      />
                                    </div>
                                    <Button
                                      size="sm"
                                      onClick={() => handleUseToil(schedule.id, schedule.employee_id)}
                                      disabled={currentToilUsage <= 0 || currentToilUsage > Math.min(shortfall, employeeToilBalance)}
                                      className="bg-purple-600 hover:bg-purple-700"
                                    >
                                      Apply TOIL
                                    </Button>
                                  </div>
                                  {currentToilUsage > 0 && (
                                    <p className="text-xs text-purple-700 mt-2">
                                      New balance after use: <span className="font-semibold">{(employeeToilBalance - currentToilUsage).toFixed(2)}h</span>
                                    </p>
                                  )}
                                  {schedule.toil_used > 0 && (
                                    <div className="mt-3 p-3 bg-purple-100 border border-purple-300 rounded-lg">
                                      <div className="flex justify-between items-center">
                                        <div>
                                          <p className="text-sm font-semibold text-purple-900">
                                            ✓ TOIL Applied: {schedule.toil_used.toFixed(2)}h
                                          </p>
                                          <p className="text-xs text-purple-700 mt-1">
                                            This TOIL has been deducted from the employee&apos;s balance
                                          </p>
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleRemoveToil(schedule.id, schedule.employee_id)}
                                          className="text-purple-700 border-purple-300 hover:bg-purple-50"
                                        >
                                          Remove TOIL
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Schedule</DialogTitle>
            <p className="text-sm text-gray-500 mt-1">Select employees and set their work schedule</p>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-semibold mb-2 block">Select Department:</Label>
              <select
                value={selectedDepartment}
                onChange={(e) => handleDepartmentChange(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Departments</option>
                {getDepartments().map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-sm font-semibold">Select Team Members:</Label>
                <button
                  type="button"
                  onClick={handleSelectAllEmployees}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  {selectedEmployees.length === getFilteredEmployees().length && getFilteredEmployees().length > 0
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
              </div>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {getFilteredEmployees().length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No employees found</p>
                ) : (
                  getFilteredEmployees().map(emp => {
                    const hasConflict = scheduleConflicts[emp.id];
                    return (
                      <div key={emp.id}>
                        <label className={`flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded ${hasConflict ? 'bg-red-50' : ''}`}>
                          <input
                            type="checkbox"
                            checked={selectedEmployees.includes(emp.id)}
                            onChange={() => toggleEmployeeSelection(emp.id)}
                            className="w-4 h-4 text-emerald-600"
                          />
                          <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                          <span className="text-xs text-gray-500">({emp.department})</span>
                        </label>
                        {hasConflict && (
                          <div className="ml-6 text-xs text-red-600 mt-1 mb-2">
                            ⚠️ Schedule already exists: {hasConflict.start_date} to {hasConflict.end_date}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''} selected
                {selectedDepartment !== 'all' && ` from ${selectedDepartment}`}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scheduled Start Time</Label>
                <Input
                  type="time"
                  value={formData.scheduled_start_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_start_time: e.target.value })}
                />
              </div>
              <div>
                <Label>Scheduled Finish Time</Label>
                <Input
                  type="time"
                  value={formData.scheduled_finish_time}
                  onChange={(e) => setFormData({ ...formData, scheduled_finish_time: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            {Object.keys(scheduleConflicts).length > 0 && (
              <div className="flex-1 text-sm text-red-600 mr-4">
                ⚠️ {Object.keys(scheduleConflicts).length} employee(s) with existing schedules
              </div>
            )}
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateSchedules} 
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={Object.keys(scheduleConflicts).length > 0 || checkingConflicts}
            >
              {checkingConflicts ? 'Checking...' : `Create ${selectedEmployees.length > 0 ? `${selectedEmployees.length} ` : ''}Schedule${selectedEmployees.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editingLog !== null} onOpenChange={() => setEditingLog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Clock Times</DialogTitle>
          </DialogHeader>
          {editingLog && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Date</Label>
                <Input value={new Date(editingLog.date).toLocaleDateString()} disabled />
              </div>
              <div>
                <Label>Clock In Time</Label>
                <Input
                  type="time"
                  value={editingLog.clockIn}
                  onChange={(e) => setEditingLog({ ...editingLog, clockIn: e.target.value })}
                />
              </div>
              <div>
                <Label>Clock Out Time</Label>
                <Input
                  type="time"
                  value={editingLog.clockOut}
                  onChange={(e) => setEditingLog({ ...editingLog, clockOut: e.target.value })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLog(null)}>Cancel</Button>
            <Button 
              onClick={() => handleEditTimeLog(editingLog.employeeId, editingLog.date, editingLog.clockIn, editingLog.clockOut)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulesEnhanced;
